// SETTINGS V2 — guest-pass list partition (GP-COLLAPSE §4.1 / §4.2).
//
// THE ONE PLACE that decides which rows are collapsed behind "Ukončené (N)".
// Pure, dependency-free, unit-tested — the collapse's security property lives
// here, not in the component.
//
// §4.2 DESIGN CONSTRAINT — hiding an ACTIVE pass must be impossible BY
// CONSTRUCTION, not by convention:
//  • The partition reads the SERVER-COMPUTED `status` string and nothing else.
//    There is no hide/archive flag, no persisted "hidden" bit, no per-row user
//    choice anywhere in the client — so there is no input that could move a live
//    credential out of the default view.
//  • Terminal membership is an EXPLICIT ALLOW-LIST of the two dead states
//    ("Expired", "Revoked") — it is NOT `status !== "Active"`. That is the
//    fail-OPEN choice: if the backend ever grows a fourth status, an unknown
//    value stays in the visible/actionable list instead of silently vanishing
//    into the collapsed group. A live credential can never be concealed by
//    drift; at worst a dead one shows up in the open list, which is harmless.
//  • Every input row lands in exactly ONE bucket (single if/else over one
//    predicate), so the two groups are disjoint and their union is the whole
//    response — nothing can be dropped.
import { GuestPass } from "../../models/guestPassModels";

/** The two server-computed dead states (`GuestPassService.ToSummary`). "Active"
 *  — including future-dated passes, which are still live credentials — is
 *  deliberately absent, and anything unrecognised is treated as NOT terminal. */
const TERMINAL_STATUSES: ReadonlyArray<string> = ["Expired", "Revoked"];

/** True only for a pass the operator can do nothing with any more. */
export const isTerminalPass = (pass: GuestPass): boolean =>
  TERMINAL_STATUSES.indexOf(pass.status) !== -1;

export interface GuestPassPartition {
  /** Rendered in the default view, always visible, never collapsible. */
  active: GuestPass[];
  /** Rendered only inside the expandable "Ukončené (N)" group. */
  terminal: GuestPass[];
}

/** Splits the list response into the actionable rows and the terminal rows.
 *  Input order is preserved within each bucket (the API orders CreatedDate DESC). */
export const partitionGuestPasses = (
  passes: GuestPass[] | undefined | null
): GuestPassPartition => {
  const active: GuestPass[] = [];
  const terminal: GuestPass[] = [];
  (passes || []).forEach((pass) => {
    if (isTerminalPass(pass)) {
      terminal.push(pass);
    } else {
      active.push(pass);
    }
  });
  return { active, terminal };
};
